import { buildShiftListStatusDisplay, resolveListCheckInState } from "./shiftListStatusDisplay";
import { isSubmittedActualState } from "./shiftActualState";

function parseDate(value) {
  if (!value) return null;
  const dt = value instanceof Date ? value : new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function normalizeSignoffToken(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

function normalizeShiftStateToken(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

/**
 * Derive list checkInState after actual/signoff changes (subset of legacyCheckinStateSql).
 */
export function deriveListCheckInState(row, now = new Date()) {
  const shiftState = normalizeShiftStateToken(row.state || row.shiftState);

  if (shiftState === "draft") return "draft";
  if (["unassigned", "unfilled", "none"].includes(shiftState)) return "unassigned";
  if (shiftState === "deleted") return "deleted";
  if (shiftState === "tentative") return "tentative";
  if (shiftState === "declined") return "declined";
  if (shiftState === "cancelled") return "cancelled";

  const signoff = normalizeSignoffToken(
    row.signoffState || row.signoff_state || "",
  );

  if (signoff === "complete") {
    const actualRaw =
      row.actualState ?? row.actual_state ?? row.chosenActualState ?? "";
    if (isSubmittedActualState(actualRaw)) return "signoffComplete";
  }
  if (signoff === "pendingreview" || signoff === "pendingsignoffreview") {
    return "signoffPendingReview";
  }

  const checkin = row.checkinDateIso || row.checkin_date || null;
  const checkout = row.checkoutDateIso || row.checkout_date || null;
  const start = parseDate(row.startDateIso || row.start_date || row.startDate);
  const end = parseDate(row.endDateIso || row.end_date || row.endDate);

  if (start && start < now && !checkin && end && end > now) return "late";

  const ended = !!(end && end < now);
  const forgotCheckout =
    !!checkin &&
    !checkout &&
    !!end &&
    new Date(end.getTime() + 24 * 60 * 60 * 1000) < now;

  if ((checkin && checkout) || (!checkin && ended) || forgotCheckout) {
    if (signoff === "complete") {
      const actualRaw =
        row.actualState ?? row.actual_state ?? row.chosenActualState ?? "";
      if (isSubmittedActualState(actualRaw)) return "signoffComplete";
    }
    if (signoff === "pendingreview" || signoff === "pendingsignoffreview") {
      return "signoffPendingReview";
    }
    return "signoffPending";
  }

  if (checkin) return "checkIn";

  const existing = row.checkInState || row.checkinState || row.check_in_state;
  return existing || "future";
}

/**
 * Merge list-row fields after POST /api/jobs/shifts/:id/actuals succeeds.
 */
export function mergeShiftRowAfterActualsSubmit(baseShift, updatedShift, payload) {
  const nextActualState =
    updatedShift?.actualState ??
    updatedShift?.actual_state ??
    payload?.actualState ??
    baseShift?.actualState ??
    baseShift?.actual_state ??
    "";
  const nextActualStart =
    updatedShift?.actualStartDate ??
    updatedShift?.actual_start_date ??
    payload?.actualStartDate ??
    baseShift?.actualStartDate ??
    baseShift?.actualStartDateIso ??
    baseShift?.actual_start_date ??
    null;
  const nextActualEnd =
    updatedShift?.actualEndDate ??
    updatedShift?.actual_end_date ??
    payload?.actualEndDate ??
    baseShift?.actualEndDate ??
    baseShift?.actualEndDateIso ??
    baseShift?.actual_end_date ??
    null;
  const nextBreak =
    updatedShift?.actualUnpaidBreakMinutes ??
    updatedShift?.actual_unpaid_break_minutes ??
    payload?.actualUnpaidBreakMinutes ??
    baseShift?.actualUnpaidBreakMinutes ??
    baseShift?.actual_unpaid_break_minutes ??
    0;

  const nextSignoffState =
    updatedShift?.signoffState ??
    updatedShift?.signoff_state ??
    baseShift?.signoffState ??
    baseShift?.signoff_state ??
    null;

  const nextActiveActualId =
    updatedShift?.activeShiftActualId ??
    updatedShift?.active_shift_actual_id ??
    baseShift?.activeShiftActualId ??
    baseShift?.active_shift_actual_id ??
    null;

  const merged = {
    ...baseShift,
    ...updatedShift,
    status:
      updatedShift?.status ??
      updatedShift?.assignment_ui_status ??
      updatedShift?.assignmentUiStatus ??
      baseShift?.status ??
      "Confirmed",
    shiftState:
      updatedShift?.shiftState ??
      updatedShift?.state ??
      baseShift?.shiftState ??
      baseShift?.state,
    state: updatedShift?.state ?? baseShift?.state,
    actualState: nextActualState,
    actual_state: nextActualState,
    chosenActualState: nextActualState,
    actualStartDate: nextActualStart,
    actualStartDateIso: nextActualStart,
    actual_start_date: nextActualStart,
    actualEndDate: nextActualEnd,
    actualEndDateIso: nextActualEnd,
    actual_end_date: nextActualEnd,
    actualUnpaidBreakMinutes: nextBreak,
    actual_unpaid_break_minutes: nextBreak,
    signoff_state: nextSignoffState,
    signoffState: nextSignoffState,
    active_shift_actual_id: nextActiveActualId,
    activeShiftActualId: nextActiveActualId,
    actualNote:
      payload?.note ??
      updatedShift?.actualNote ??
      updatedShift?.note ??
      baseShift?.actualNote ??
      "",
  };

  const checkInState =
    updatedShift?.checkInState ??
    updatedShift?.checkinState ??
    updatedShift?.check_in_state ??
    resolveListCheckInState(merged) ??
    deriveListCheckInState(merged);
  merged.checkInState = checkInState;
  merged.checkinState = checkInState;
  merged.check_in_state = checkInState;
  merged.statusDisplay = buildShiftListStatusDisplay(merged);

  return merged;
}
